import Config from '@config';
import {
  OrganizationBankAccount,
  OrganizationBankAccountRequest,
} from '@gasbi/shared/web/RbacOrganization';

import { injectSupplySlice } from '@/app/supplyApi';
import { FiltersGrid, Pagination } from '@/features/types';
import {
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@/features/utils';

const api = injectSupplySlice({
  baseUrl: `${Config.VITE_API_URL}/organization/bank-accounts`,
  tagTypes: ['OrganizationBankAccounts'],
  endpoints: builder => ({
    getOrganizationBankAccounts: builder.infiniteQuery<
      { items: OrganizationBankAccount[]; total: number },
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '',
        method: 'GET',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['OrganizationBankAccounts'],
    }),
    createOrganizationBankAccount: builder.mutation<
      string,
      OrganizationBankAccountRequest
    >({
      query: data => ({ url: '', method: 'POST', data }),
      invalidatesTags: ['OrganizationBankAccounts'],
    }),
    updateOrganizationBankAccount: builder.mutation<
      void,
      { id: string } & OrganizationBankAccountRequest
    >({
      query: ({ id, ...data }) => ({
        url: `/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['OrganizationBankAccounts'],
    }),
    deactivateOrganizationBankAccount: builder.mutation<void, string>({
      query: id => ({ url: `/${id}/deactivate`, method: 'POST' }),
      invalidatesTags: ['OrganizationBankAccounts'],
    }),
  }),
});

export const organizationBankAccountsApi = api;

const useNormalizedGetOrganizationBankAccountsInfiniteQuery =
  normalizeInfiniteQueryData(
    api.useGetOrganizationBankAccountsInfiniteQuery,
    'items',
  );

export const useGetOrganizationBankAccountsInfiniteQuery = (
  queryArg?: FiltersGrid,
  options?: any,
) => {
  const normalized = useNormalizedGetOrganizationBankAccountsInfiniteQuery(
    queryArg,
    options,
  );
  const { data } = api.useGetOrganizationBankAccountsInfiniteQuery(
    queryArg,
    options,
  );

  return {
    ...normalized,
    total: data?.pages?.[0]?.total ?? normalized.data.length,
  };
};

export const {
  useCreateOrganizationBankAccountMutation,
  useUpdateOrganizationBankAccountMutation,
  useDeactivateOrganizationBankAccountMutation,
} = api;
